import React, { useCallback, useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import {
  FaBan,
  FaCheck,
  FaCheckDouble,
  FaClipboardCheck,
  FaPaperPlane,
  FaPlus,
  FaTimesCircle,
  FaTrash,
} from "react-icons/fa"
import api from "../../libs/axios"
import { useDebounce } from "../../hooks/useDebounce"
import { useAuth } from "../../provider/auth.provider"
import { useConfirm } from "../../provider/confirm.provider"
import { Pagination, PaginationType } from "../../utils/pagination.utils"
import {
  apiError,
  dateText,
  fetchPaged,
  FilterOption,
  fullName,
  HolderType,
  materialFilters,
  materialMasters,
  money,
  qty,
} from "./shared/material.api"
import {
  AsyncPicker,
  Cell,
  DataTable,
  DetailRow,
  ExpandAction,
  Field,
  FilterField,
  FilterRow,
  GhostButton,
  HolderChip,
  HolderPicker,
  IconAction,
  ListCard,
  OffCanvas,
  PrimaryButton,
  RefreshButton,
  ScopeNote,
  SearchBox,
  StatusBadge,
  StatusTabs,
  Td,
  TextArea,
  TextInput,
  Th,
} from "./shared/MaterialUI"

/**
 * Material Consumption Report (MCR).
 *
 * The only way consumption is booked. A report in draft or waiting on approval
 * is a claim; stock leaves the holder's balance at the SECOND approval and not
 * a moment before. The two levels are separate permissions, and the person who
 * signed level 1 cannot also sign level 2.
 *
 * Reports also arrive here from the INC visits tab: installed material mirrors
 * into a draft MCR for that visit, which is submitted and approved the same way.
 */

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "submitted", label: "Awaiting L1" },
  { key: "approved_l1", label: "Awaiting L2" },
  { key: "approved", label: "Approved" },
  { key: "rejected,cancelled", label: "Closed" },
]

interface LineDraft {
  key: string
  item: FilterOption | null
  quantity: string
  is_client_supplied: boolean
  remarks: string
}

const blankLine = (): LineDraft => ({
  key: Math.random().toString(36).slice(2),
  item: null,
  quantity: "",
  is_client_supplied: false,
  remarks: "",
})

const MaterialConsumptionsPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const { confirm, prompt } = useConfirm()
  const canCreate = hasPermission("MaterialConsumption", "create")
  const canL1 = hasPermission("MaterialConsumption", "approve_l1")
  const canL2 = hasPermission("MaterialConsumption", "approve_l2")
  const canCancel = hasPermission("MaterialConsumption", "cancel")

  const [rows, setRows] = useState<any[]>([])
  const [pagination, setPagination] = useState<PaginationType | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [status, setStatus] = useState("")
  const [project, setProject] = useState<FilterOption | null>(null)
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search, 400)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [showForm, setShowForm] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchPaged("/materials/consumptions", {
        page,
        limit,
        status: status || undefined,
        project_id: project?.id,
        search: debouncedSearch.trim() || undefined,
      })
      setRows(result.rows)
      setPagination(result.pagination)
    } catch (e: any) {
      toast.error(apiError(e, "Failed to load consumption reports"))
    } finally {
      setLoading(false)
    }
  }, [page, limit, status, project, debouncedSearch])

  useEffect(() => {
    load()
  }, [load])

  const act = async (row: any, action: "submit" | "approve-l1" | "approve-l2" | "reject" | "cancel") => {
    let body: Record<string, any> = {}

    if (action === "reject" || action === "cancel") {
      const entered = await prompt({
        title: action === "reject" ? "Reject consumption report" : "Cancel consumption report",
        message: "The site supervisor will see your reason.",
        label: "Reason",
        required: true,
        multiline: true,
        confirmText: action === "reject" ? "Reject" : "Cancel report",
        cancelText: "Go back",
        variant: "warning",
      })
      if (entered === null) return
      body = { remarks: entered, reason: entered }
    }

    if (action === "approve-l2") {
      const ok = await confirm({
        title: "Second approval - this consumes the stock",
        message: (
          <>
            Approving at level 2 takes the reported quantities off{" "}
            <strong>{row.holder_label || "the holder"}</strong> for good. Client-supplied lines are recorded but move
            nothing.
          </>
        ),
        warning:
          "If the holder does not hold enough, the approval is refused - the material was never recorded as issued to them.",
        confirmText: "Approve and consume",
        cancelText: "Not yet",
        variant: "warning",
      })
      if (!ok) return
    }

    setBusyId(row.id)
    try {
      const res = await api.post(`/materials/consumptions/${row.id}/${action}`, body)
      toast.success(res.data?.message || "Done")
      load()
    } catch (e: any) {
      toast.error(apiError(e, "The action could not be completed"))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <ListCard
        icon={<FaClipboardCheck className="w-5 h-5" />}
        title="Material Consumption Reports (MCR)"
        subtitle="What was actually installed at site. Two approvals are required, and the second one consumes the stock. Client-supplied material is recorded but posts no movement."
        actions={
          <>
            <RefreshButton loading={loading} onClick={load} />
            {canCreate ? (
              <PrimaryButton onClick={() => setShowForm(true)}>
                <FaPlus className="w-4 h-4" /> New Report
              </PrimaryButton>
            ) : null}
          </>
        }
        filters={
          <>
            <FilterRow>
              <FilterField label="Search" grow>
                <SearchBox
                  value={search}
                  onChange={(v) => {
                    setSearch(v)
                    setPage(1)
                  }}
                  placeholder="MCR number, site..."
                />
              </FilterField>
              <FilterField label="Project">
                <AsyncPicker
                  value={project}
                  onChange={(o) => {
                    setProject(o)
                    setPage(1)
                  }}
                  fetcher={(s) => materialFilters.projects(s)}
                  placeholder="All my projects"
                  emptyHint="You are not assigned to any project"
                />
              </FilterField>
            </FilterRow>
            <StatusTabs
              tabs={STATUS_TABS}
              value={status}
              onChange={(k) => {
                setStatus(k)
                setPage(1)
              }}
            />
          </>
        }
        footer={
          pagination ? (
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l)
                setPage(1)
              }}
            />
          ) : null
        }
      >
        <DataTable
          colSpan={8}
          loading={loading}
          empty={rows.length === 0}
          loadingText="Loading consumption reports..."
          emptyText="No consumption reports found"
          emptyHint={<ScopeNote />}
          emptyIcon={<FaClipboardCheck />}
          head={
            <>
              <Th>Actions</Th>
              <Th>Report</Th>
              <Th>Consumed From</Th>
              <Th>Date</Th>
              <Th>Lines</Th>
              <Th>Reported By</Th>
              <Th>Approvals</Th>
              <Th>Status</Th>
            </>
          }
        >
          {rows.map((row) => (
            <React.Fragment key={row.id}>
              <tr className="hover:bg-gray-50 transition-colors">
                <Cell>
                  <div className="flex items-center gap-1">
                    <ExpandAction
                      open={expanded === row.id}
                      onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
                      what="items"
                    />
                    {row.status === "draft" && canCreate ? (
                      <IconAction title="Submit for approval" disabled={busyId === row.id} onClick={() => act(row, "submit")}>
                        <FaPaperPlane />
                      </IconAction>
                    ) : null}
                    {row.status === "submitted" && canL1 ? (
                      <IconAction title="Approve L1" tone="teal" disabled={busyId === row.id} onClick={() => act(row, "approve-l1")}>
                        <FaCheck />
                      </IconAction>
                    ) : null}
                    {row.status === "approved_l1" && canL2 ? (
                      <IconAction
                        title="Approve L2 (consumes the stock)"
                        tone="green"
                        disabled={busyId === row.id}
                        onClick={() => act(row, "approve-l2")}
                      >
                        <FaCheckDouble />
                      </IconAction>
                    ) : null}
                    {["submitted", "approved_l1"].includes(row.status) && (canL1 || canL2) ? (
                      <IconAction title="Reject" tone="red" disabled={busyId === row.id} onClick={() => act(row, "reject")}>
                        <FaTimesCircle />
                      </IconAction>
                    ) : null}
                    {!["approved", "cancelled"].includes(row.status) && canCancel ? (
                      <IconAction title="Cancel report" tone="gray" disabled={busyId === row.id} onClick={() => act(row, "cancel")}>
                        <FaBan />
                      </IconAction>
                    ) : null}
                  </div>
                </Cell>
                <Cell>
                  <div className="font-medium">{row.consumption_number}</div>
                  <div className="text-xs text-gray-500">
                    {row.project?.project_short_name || row.project?.project_name || "-"}
                    {row.visit_id ? <span className="ml-1 text-blue-600">(from visit)</span> : null}
                  </div>
                </Cell>
                <Cell>
                  <HolderChip type={row.holder_type} label={row.holder_label} code={row.holder_code} />
                </Cell>
                <Cell>{dateText(row.consumption_date)}</Cell>
                <Cell>{(row.items || []).length}</Cell>
                <Cell>{fullName(row.reporter)}</Cell>
                <Cell className="text-xs">
                  <div>L1: {row.approved_l1_by ? fullName(row.approver_l1) : "-"}</div>
                  <div>L2: {row.approved_l2_by ? fullName(row.approver_l2) : "-"}</div>
                </Cell>
                <Cell>
                  <StatusBadge status={row.status} />
                </Cell>
              </tr>
              {expanded === row.id ? (
                <DetailRow colSpan={8}>
                  <ConsumptionDetail row={row} />
                </DetailRow>
              ) : null}
            </React.Fragment>
          ))}
        </DataTable>
      </ListCard>

      {showForm ? (
        <ConsumptionForm
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false)
            setPage(1)
            load()
          }}
        />
      ) : null}
    </div>
  )
}

const ConsumptionDetail: React.FC<{ row: any }> = ({ row }) => (
  <div className="space-y-3 py-2">
    <table className="min-w-full text-sm bg-white border border-gray-200 rounded">
      <thead className="bg-gray-100">
        <tr>
          <Th>Item</Th>
          <Th>Quantity</Th>
          <Th>Rate</Th>
          <Th>Source</Th>
          <Th>Billable</Th>
          <Th>Remarks</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {(row.items || []).map((line: any) => (
          <tr key={line.id}>
            <Td>
              {line.item?.item_name || `#${line.item_id}`}
              {line.item?.item_code ? <span className="text-xs text-gray-400"> ({line.item.item_code})</span> : null}
            </Td>
            <Td>{qty(line.quantity)}</Td>
            <Td>{line.unit_rate ? money(line.unit_rate) : "-"}</Td>
            <Td>
              {line.is_client_supplied ? (
                <span className="text-xs px-1.5 py-0.5 rounded bg-violet-100 text-violet-800">Client supplied</span>
              ) : (
                <span className="text-xs text-gray-500">Company stock</span>
              )}
            </Td>
            <Td>{line.is_billable ? "Yes" : "No"}</Td>
            <Td className="text-gray-500">{line.remarks || "-"}</Td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="text-[11px] text-gray-500">
      Client-supplied lines appear on the report and in the BOQ but post no stock movement - that material was never on
      the company's books.
    </p>
    {row.rejection_remarks ? (
      <p className="text-xs text-red-700">
        <span className="font-medium">Rejected:</span> {row.rejection_remarks}
      </p>
    ) : null}
  </div>
)

const ConsumptionForm: React.FC<{ onClose: () => void; onSaved: () => void }> = ({ onClose, onSaved }) => {
  const [saving, setSaving] = useState(false)
  const [project, setProject] = useState<FilterOption | null>(null)
  const [holderType, setHolderType] = useState<HolderType>("site")
  const [holder, setHolder] = useState<FilterOption | null>(null)
  const [consumptionDate, setConsumptionDate] = useState(new Date().toISOString().slice(0, 10))
  const [remarks, setRemarks] = useState("")
  const [lines, setLines] = useState<LineDraft[]>([blankLine()])
  const [available, setAvailable] = useState<Record<number, number>>({})

  const fetchers = useMemo(
    () => ({
      warehouse: (s: string) => materialMasters.warehouses(s),
      staff: (s: string) => materialFilters.staff(project ? String(project.id) : undefined, s),
      site: (s: string) => materialFilters.sites(project ? String(project.id) : undefined, s),
    }),
    [project],
  )

  // Show what the holder is actually carrying, so a report that will be
  // refused at level 2 is visible while it is being written rather than two
  // approvals later.
  useEffect(() => {
    if (!holder || !project) {
      setAvailable({})
      return
    }
    let cancelled = false
    api
      .get("/materials/issues/availability", {
        params: { holder_type: holderType, holder_id: holder.id, project_id: project.id },
      })
      .then((res) => {
        if (cancelled) return
        const map: Record<number, number> = {}
        ;(res.data?.data?.balances || []).forEach((b: any) => {
          map[Number(b.item_id)] = Number(b.available_quantity || 0)
        })
        setAvailable(map)
      })
      .catch(() => {
        if (!cancelled) setAvailable({})
      })
    return () => {
      cancelled = true
    }
  }, [holder, holderType, project])

  const submit = async (submitForApproval: boolean) => {
    if (!project) return toast.error("Pick a project")
    if (!holder) return toast.error("Pick whose stock was consumed")
    const validLines = lines.filter((l) => l.item && Number(l.quantity) > 0)
    if (!validLines.length) return toast.error("Add at least one item line with a quantity")

    setSaving(true)
    try {
      const res = await api.post("/materials/consumptions", {
        project_id: project.id,
        holder_type: holderType,
        holder_id: holder.id,
        inc_site_id: holderType === "site" ? holder.id : undefined,
        consumption_date: consumptionDate,
        remarks: remarks || undefined,
        submit: submitForApproval,
        items: validLines.map((l) => ({
          item_id: l.item!.id,
          quantity: Number(l.quantity),
          is_client_supplied: l.is_client_supplied,
          remarks: l.remarks || null,
        })),
      })
      toast.success(res.data?.message || "Consumption report created")
      onSaved()
    } catch (e: any) {
      toast.error(apiError(e, "Failed to create the consumption report"))
    } finally {
      setSaving(false)
    }
  }

  return (
    <OffCanvas
      onClose={onClose}
      title="New consumption report"
      subtitle="Report what was installed. Nothing leaves the books until both approvals are in."
      width="xl"
      footer={
        <>
          <GhostButton onClick={() => submit(false)} disabled={saving}>
            Save as draft
          </GhostButton>
          <PrimaryButton loading={saving} onClick={() => submit(true)}>
            Submit for approval
          </PrimaryButton>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Project" required>
          <AsyncPicker
            value={project}
            onChange={setProject}
            fetcher={(s) => materialFilters.projects(s)}
            placeholder="Select project..."
            emptyHint="You are not assigned to any project"
          />
        </Field>
        <Field label="Consumed from" required hint="Normally the site; a staff member consuming directly is allowed">
          <HolderPicker
            holderType={holderType}
            onHolderTypeChange={setHolderType}
            value={holder}
            onChange={setHolder}
            fetchers={fetchers}
            allowed={["site", "staff", "warehouse"]}
            dependency={project?.id}
            disabled={!project}
          />
        </Field>
        <Field label="Consumption date" required>
          <TextInput type="date" value={consumptionDate} onChange={(e) => setConsumptionDate(e.target.value)} />
        </Field>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-800">Installed material</h3>
          <GhostButton onClick={() => setLines((l) => [...l, blankLine()])}>
            <FaPlus /> Add line
          </GhostButton>
        </div>
        <div className="border border-gray-200 rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th className="w-2/5">Item</Th>
                <Th>Quantity</Th>
                <Th>Holder has</Th>
                <Th>Client supplied</Th>
                <Th>Remarks</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map((line, index) => {
                const avail = line.item ? available[line.item.id] ?? 0 : null
                const short =
                  line.item &&
                  !line.is_client_supplied &&
                  Number(line.quantity) > 0 &&
                  (avail ?? 0) < Number(line.quantity)
                return (
                  <tr key={line.key}>
                    <Td>
                      <AsyncPicker
                        value={line.item}
                        onChange={(o) => setLines((ls) => ls.map((l, i) => (i === index ? { ...l, item: o } : l)))}
                        fetcher={(s) => materialMasters.items(s)}
                        placeholder="Select item..."
                      />
                    </Td>
                    <Td>
                      <TextInput
                        type="number"
                        min="0"
                        step="0.001"
                        value={line.quantity}
                        onChange={(e) =>
                          setLines((ls) => ls.map((l, i) => (i === index ? { ...l, quantity: e.target.value } : l)))
                        }
                      />
                    </Td>
                    <Td className={short ? "text-amber-700 font-medium" : "text-gray-500"}>
                      {line.is_client_supplied ? "n/a" : line.item ? qty(avail) : "-"}
                    </Td>
                    <Td>
                      <input
                        type="checkbox"
                        checked={line.is_client_supplied}
                        onChange={(e) =>
                          setLines((ls) =>
                            ls.map((l, i) => (i === index ? { ...l, is_client_supplied: e.target.checked } : l)),
                          )
                        }
                      />
                    </Td>
                    <Td>
                      <TextInput
                        value={line.remarks}
                        onChange={(e) =>
                          setLines((ls) => ls.map((l, i) => (i === index ? { ...l, remarks: e.target.value } : l)))
                        }
                      />
                    </Td>
                    <Td>
                      <button
                        onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((_, i) => i !== index) : ls))}
                        className="text-red-500 hover:text-red-700 disabled:opacity-30"
                        disabled={lines.length === 1}
                      >
                        <FaTrash />
                      </button>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-gray-500 mt-1">
          Tick "client supplied" for the client's own products and equipment. Those lines are reported and can be
          billed, but they post no stock movement. Whether a line is billable is taken from the item master.
        </p>
      </div>

      <Field label="Remarks">
        <TextArea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </Field>
    </OffCanvas>
  )
}

export default MaterialConsumptionsPage
